import { useUserContext } from '@nrdy-marketing-engine/server/src/user/context';
import { useEffect, useMemo } from 'react';
import { CartStep } from '../../../@types/cart-step';
import { trackCartFailure } from '../../lib/track-service/track-cart';
import { ConvertUserToClientState } from '../use-convert-user-to-client';

interface CartStepperProps {
  cartLoading: boolean;
  catalogItemError: Error;
  catalogItemPresent: boolean;
  convertUserToClientState: ConvertUserToClientState;
  missingCatalogItem: boolean;
  onChangeStep: (number) => void;
}

export default function useCartStepper({
  onChangeStep,
  cartLoading,
  missingCatalogItem,
  catalogItemPresent,
  convertUserToClientState,
  catalogItemError,
}: CartStepperProps) {
  const { user, loading: userLoading } = useUserContext();
  const shouldShowLoading = useMemo(
    () =>
      userLoading || cartLoading || convertUserToClientState === ConvertUserToClientState.LOADING,
    [userLoading, cartLoading, convertUserToClientState]
  );

  const cartStep = useMemo(() => {
    // loading step
    if (shouldShowLoading) return CartStep.Loading;

    // signed out step
    if (!user) return CartStep.Account;

    // cart user error steps
    if (user) {
      if (user?.entity?.role === 'tutor' || user?.entity?.role === 'student') {
        trackCartFailure(
          'cart_invalid_user_failure',
          { entityId: user.entity.id, role: user.entity.role },
          'Invalid user role'
        );
        return CartStep.Account;
      }

      if (convertUserToClientState === ConvertUserToClientState.FAIL) {
        return CartStep.Account;
      }
    }

    // missing item step
    if (user && missingCatalogItem) {
      trackCartFailure(
        'cart_missing_failure',
        {
          clientUuid: user?.entity?.client_uuid,
          error: catalogItemError,
        },
        `Error: ${catalogItemError}`
      );
      return CartStep.Missing;
    }

    // checkout details step
    if (user && catalogItemPresent) {
      return CartStep.Details;
    }
  }, [catalogItemPresent, convertUserToClientState, missingCatalogItem, user, shouldShowLoading]);

  useEffect(
    function _updateStep() {
      // let the onChangeStep callback know what step we are on
      if (cartStep === CartStep.Details) onChangeStep(1);
    },
    [cartStep, onChangeStep]
  );

  return { cartStep };
}
